import { type MbscCalendarColor, type MbscResource } from '@mobiscroll/react';
import { addDays, compareAsc, eachDayOfInterval, isSameDay } from 'date-fns';

import {
  clusterzuordnung,
  PlanungsinformationFromThemaRouter,
  RessourcenBlockungen,
  RessourcenBlockungType,
  ressourcenBlockungTypes,
  type ZeitlicherAblauf,
  type Zeitraum,
} from '../../../dtos';
import { calendarColors, elevateColorPalette } from '../../../theme/colorPalette';
import { printClusterzuordnungLabel } from '../../../utils';
import { setTimeFromTimeString } from '../../../utils/dateAndIntervalHelpers';
import { getZeitraeumeStartEnd, isDateInZeitraum } from '../../../utils/zeitraum';
import { CalendarEvent, CalendarResourceType, calendarResourceTypes, isExperteEvent } from '../ResourceCalendar';
import {
  createAblaufColors,
  createCalendarColors,
  getSelectedSeminarraumLocationKuerzel,
} from '../ResourceCalendar/createCalendarColors';
import {
  AbstractCalendarResource,
  CalendarExperteResource,
  CalendarGruppenraumResource,
  CalendarSeminarraumResource,
  CalendarStandortResource,
} from '../ResourceCalendar/helperClasses/CalendarResource';
import { LocationResource } from '../ResourceCalendar/helperClasses/CalendarResource/LocationResource';
import { content } from './VeranstaltungRessourcenCalendar/VeranstaltungRessourcenCalendar.content';
import {
  type CollapseInfo,
  type ErsatztrainerResourceGroup,
  type ExperteResourceGroup,
  type GruppenraumResourceGroup,
  type HaupttrainerResourceGroup,
  isErsatztrainerResource,
  isHaupttrainerResource,
  isNebentrainerResource,
  type NebentrainerResourceGroup,
  type SeminarraumResourceGroup,
  type StandortResourceGroup,
} from './VivaRessourcenCalendars.utils.types';

const getGroupDefaultValues = (
  collapseInfo: CollapseInfo,
): {
  expertenGroup: ExperteResourceGroup;
  seminarraeumeGroup: SeminarraumResourceGroup;
  gruppenraeumeGroup: GruppenraumResourceGroup;
  standorteGroup: StandortResourceGroup;
} => {
  const seminarraeumeGroup: SeminarraumResourceGroup = {
    id: calendarResourceTypes.SEMINARRAUM,
    name: content.calenderResourceGroupLabels.SEMINARRAUM,
    children: [],
    collapsed: collapseInfo.seminarraum,
    background: calendarColors.accordion,
    color: calendarColors.accordionText,
    eventCreation: false,
  };

  const gruppenraeumeGroup: GruppenraumResourceGroup = {
    id: calendarResourceTypes.GRUPPENRAUM,
    name: content.calenderResourceGroupLabels.GRUPPENRAUM,
    children: [],
    collapsed: collapseInfo.gruppenraum,
    background: calendarColors.accordion,
    color: calendarColors.accordionText,
    eventCreation: false,
  };

  const standorteGroup: StandortResourceGroup = {
    id: calendarResourceTypes.STANDORT,
    name: content.calenderResourceGroupLabels.STANDORT,
    children: [],
    collapsed: collapseInfo.standort,
    background: calendarColors.accordion,
    color: calendarColors.accordionText,
    eventCreation: false,
  };

  const expertenGroup: ExperteResourceGroup = {
    id: calendarResourceTypes.EXPERTE,
    name: content.calenderResourceGroupLabels.EXPERTE,
    collapsed: collapseInfo.experte,
    children: [],
    background: calendarColors.accordion,
    color: calendarColors.accordionText,
    eventCreation: false,
  };

  return {
    expertenGroup,
    seminarraeumeGroup,
    gruppenraeumeGroup,
    standorteGroup,
  };
};

const getExpertenSubGroupDefaults = (
  planungsinformation: PlanungsinformationFromThemaRouter,
  collapseInfo: CollapseInfo,
): {
  haupttrainerGroup: HaupttrainerResourceGroup;
  nebentrainerGroup: NebentrainerResourceGroup;
  ersatztrainerGroup: ErsatztrainerResourceGroup;
} => {
  const clusterAnteile = planungsinformation.clusteranteil?.split('/') ?? [];
  const clusteranteilA = clusterAnteile[0];
  const clusteranteilB = clusterAnteile[1];

  const haupttrainerGroup: HaupttrainerResourceGroup = {
    id: clusterzuordnung.HAUPTTRAINER,
    name: `${printClusterzuordnungLabel(clusterzuordnung.HAUPTTRAINER)} - Cluster ${clusteranteilA}%`,
    children: [],
    background: elevateColorPalette.surface.disabled,
    color: elevateColorPalette.text.primaryOnAccent,
    collapsed: collapseInfo.haupttrainer,
  };

  const nebentrainerGroup: NebentrainerResourceGroup = {
    id: clusterzuordnung.NEBENTRAINER,
    name: `${printClusterzuordnungLabel(clusterzuordnung.NEBENTRAINER)} - Cluster ${clusteranteilB}%`,
    children: [],
    background: elevateColorPalette.surface.disabled,
    color: elevateColorPalette.text.primaryOnAccent,
    collapsed: collapseInfo.nebentrainer,
  };

  const ersatztrainerGroup: ErsatztrainerResourceGroup = {
    id: clusterzuordnung.ERSATZTRAINER,
    name: `${printClusterzuordnungLabel(clusterzuordnung.ERSATZTRAINER)} - Cluster C`,
    children: [],
    background: elevateColorPalette.surface.disabled,
    color: elevateColorPalette.text.primaryOnAccent,
    collapsed: collapseInfo.ersatztrainer,
  };

  return {
    haupttrainerGroup,
    nebentrainerGroup,
    ersatztrainerGroup,
  };
};

type GroupCalendarResourcesProps = {
  seminarraumResources: CalendarSeminarraumResource[];
  gruppenraumResources: CalendarGruppenraumResource[];
  standortResources: CalendarStandortResource[];
  experteResources: CalendarExperteResource[];
  collapseInfo: CollapseInfo;
  planungsinformation: PlanungsinformationFromThemaRouter;
  isOnlineVeranstaltung: boolean;
  isModultermin: boolean;
  events: CalendarEvent[];
  includeGruppenraeume: boolean;
  includeStandorte?: boolean;
  includeExperten?: boolean;
};

export const groupCalendarResources = ({
  seminarraumResources,
  gruppenraumResources,
  standortResources,
  experteResources,
  collapseInfo,
  planungsinformation,
  isOnlineVeranstaltung,
  isModultermin,
  events,
  includeGruppenraeume,
  includeStandorte = true,
  includeExperten = true,
}: GroupCalendarResourcesProps): MbscResource[] => {
  const { expertenGroup, seminarraeumeGroup, gruppenraeumeGroup, standorteGroup } = getGroupDefaultValues(collapseInfo);
  const { haupttrainerGroup, nebentrainerGroup, ersatztrainerGroup } = getExpertenSubGroupDefaults(
    planungsinformation,
    collapseInfo,
  );
  const selectedSeminarraumLocationKuerzel = getSelectedSeminarraumLocationKuerzel(events);

  const seminarraeumeByLocationMap = new Map<string, CalendarSeminarraumResource[]>();
  const gruppenraeumeByLocationMap = new Map<string, CalendarGruppenraumResource[]>();

  for (const resource of seminarraumResources) {
    const locationKuerzel = resource.location.kuerzel;
    if (!seminarraeumeByLocationMap.has(locationKuerzel)) {
      seminarraeumeByLocationMap.set(locationKuerzel, []);
    }

    const locationResources = seminarraeumeByLocationMap.get(locationKuerzel);
    if (locationResources) {
      locationResources.push(resource);
    }
  }

  for (const resource of gruppenraumResources) {
    // only show gruppenräume if no seminarraum is selected yet (e.g. at the beginning of the selection process) OR if it's the same location as the selected seminarraum
    if (!selectedSeminarraumLocationKuerzel || resource.location.kuerzel === selectedSeminarraumLocationKuerzel) {
      const locationKuerzel = resource.location.kuerzel;
      if (!gruppenraeumeByLocationMap.has(locationKuerzel)) {
        gruppenraeumeByLocationMap.set(locationKuerzel, []);
      }

      const locationResources = gruppenraeumeByLocationMap.get(locationKuerzel);
      if (locationResources) {
        locationResources.push(resource);
      }
    }
  }

  for (const resource of standortResources) {
    standorteGroup.children.push(resource);
  }

  for (const resource of experteResources) {
    if (isHaupttrainerResource(resource)) {
      haupttrainerGroup.children.push(resource);
    } else if (isNebentrainerResource(resource)) {
      nebentrainerGroup.children.push(resource);
    } else if (isErsatztrainerResource(resource)) {
      ersatztrainerGroup.children.push(resource);
    }
  }

  // locations hinzufügen inkl. seminarräume & gruppenräume
  for (const [, seminarraeume] of seminarraeumeByLocationMap) {
    if (seminarraeume.length > 0) {
      const { location } = seminarraeume[0];
      const locationResource = new LocationResource<CalendarSeminarraumResource>(
        location,
        calendarResourceTypes.SEMINARRAUM,
        seminarraeume,
      );
      seminarraeumeGroup.children.push(locationResource);
    }
  }

  for (const [, gruppenraeume] of gruppenraeumeByLocationMap) {
    if (gruppenraeume.length > 0) {
      const { location } = gruppenraeume[0];
      const locationResource = new LocationResource<CalendarGruppenraumResource>(
        location,
        calendarResourceTypes.GRUPPENRAUM,
        gruppenraeume,
      );
      gruppenraeumeGroup.children.push(locationResource);
    }
  }

  if (haupttrainerGroup.children.length > 0) expertenGroup.children.push(haupttrainerGroup);
  if (nebentrainerGroup.children.length > 0) expertenGroup.children.push(nebentrainerGroup);
  if (ersatztrainerGroup.children.length > 0) expertenGroup.children.push(ersatztrainerGroup);

  const result: MbscResource[] = [];

  if (isOnlineVeranstaltung) {
    if (includeExperten) result.push(expertenGroup);
  } else {
    result.push(seminarraeumeGroup);
    if (includeGruppenraeume) result.push(gruppenraeumeGroup);
    if (!isModultermin && includeStandorte) result.push(standorteGroup);
    if (includeExperten) result.push(expertenGroup);
  }

  return result;
};

export const createCalendarColorsWithSelectionBounds = (
  seminarraumResources: CalendarSeminarraumResource[],
  gruppenraumResources: CalendarGruppenraumResource[],
  standortResources: CalendarStandortResource[],
  experteResources: CalendarExperteResource[],
  events: CalendarEvent[],
  ablauf: Zeitraum[],
  timeRange: { start: Date; end: Date } | null,
): MbscCalendarColor[] => {
  const colors = createCalendarColors({
    seminarraumResources,
    gruppenraumResources,
    standortResources,
    experteResources,
    events,
    timeRange,
  });
  const currentSelection = events.filter((event) => event.isCurrentSelection);

  for (const zeitraum of ablauf) {
    for (const day of eachDayOfInterval(zeitraum)) {
      colors.push(createAblaufColors(day, currentSelection));
    }
  }

  return colors;
};

const convertToResourceBlockungType = (type: CalendarResourceType): RessourcenBlockungType => {
  if (type === calendarResourceTypes.GRUPPENRAUM || type === calendarResourceTypes.SEMINARRAUM) {
    return ressourcenBlockungTypes.RAUM;
  }

  return type;
};

export const convertCurrentlySelectedCalendarEventsToBlockungen = (
  events: readonly CalendarEvent[],
  ablauf: Zeitraum[],
): RessourcenBlockungen => {
  const blockungen: RessourcenBlockungen = [];

  for (const event of events) {
    if (event.type === calendarResourceTypes.EXPERTE) {
      blockungen.push({
        ressourceSapId: event.ressourceSapId,
        type: convertToResourceBlockungType(event.type),
        start: event.start,
        end: event.end,
      });
    } else {
      for (const day of eachDayOfInterval(event)) {
        const zeitraum = ablauf.find((ztr) => isDateInZeitraum(ztr, day));
        if (!zeitraum) {
          throw new Error(`AblaufTag für Ressource ${event.ressourceSapId} an Tag ${day.toISOString()} nicht gefunden`);
        }

        blockungen.push({
          ressourceSapId: event.ressourceSapId,
          type: convertToResourceBlockungType(event.type),
          ...zeitraum,
        });
      }
    }
  }

  return blockungen;
};

export const inferAblaufFromStartDate = (startDate: Date, zeitlicherAblauf: ZeitlicherAblauf): Zeitraum[] => {
  if (!zeitlicherAblauf) {
    return [];
  }

  return zeitlicherAblauf.map((template) => {
    const date = addDays(startDate, template.tagNr - 1);
    const zeitraum = {
      start: setTimeFromTimeString(date, template.startZeit),
      end: setTimeFromTimeString(date, template.endZeit),
    };
    return zeitraum;
  });
};

export const inferAblaufFromEvents = (
  events: readonly CalendarEvent[],
  zeitlicherAblauf: ZeitlicherAblauf,
  shiftedAblauf?: readonly Zeitraum[],
): Zeitraum[] => {
  if (events.length === 0) {
    return [];
  }

  const newAblauf: Zeitraum[] = [];
  const eventDays: Date[] = [];
  for (const event of events) {
    for (const day of eachDayOfInterval(event)) {
      if (!eventDays.some((ed) => isSameDay(ed, day))) {
        eventDays.push(day);
      }
    }
  }

  eventDays.sort(compareAsc);

  for (const [idx, date] of eventDays.entries()) {
    const experteEventsForDate = events.filter((event) => isDateInZeitraum(event, date) && isExperteEvent(event));
    if (experteEventsForDate.length > 0) {
      const { start, end } = getZeitraeumeStartEnd(experteEventsForDate);

      const zeitraum = { start, end };
      newAblauf.push(zeitraum);
    } else {
      const template = (zeitlicherAblauf ?? []).at(idx);
      if (!template) {
        throw new Error(`Kein Zeitlicher Ablauf Eintrag für Tag ${idx + 1} vorhanden`);
      }
      const zeitraum = {
        start: setTimeFromTimeString(date, template.startZeit),
        end: setTimeFromTimeString(date, template.endZeit),
      };
      newAblauf.push(zeitraum);
    }
  }

  // compare newAblauf with shiftedAblauf and adjust if necessary
  // check if newAblauf is missing a day that is in shiftedAblauf
  if (shiftedAblauf) {
    for (const shifted of shiftedAblauf) {
      const existsInNewAblauf = newAblauf.some((ztr) => isSameDay(ztr.start, shifted.start));
      if (!existsInNewAblauf) {
        newAblauf.push(shifted);
      }
    }
  }

  return newAblauf;
};

export function findResourceOrThrow(
  seminarraumResources: CalendarSeminarraumResource[],
  gruppenraumResources: CalendarGruppenraumResource[],
  standortResources: CalendarStandortResource[],
  experteResources: CalendarExperteResource[],
  id: string,
): AbstractCalendarResource {
  const resources = [...seminarraumResources, ...gruppenraumResources, ...standortResources, ...experteResources];
  const resource = resources.find((res) => res.id === id);
  if (!resource) {
    throw new Error(`Resource with id ${id} not found`);
  }

  return resource;
}

export function isEventUpdateAllowed(newEvent: CalendarEvent, oldEvent: CalendarEvent, ablauf: Zeitraum[]): boolean {
  if (newEvent.type !== oldEvent.type) {
    return false;
  }

  if (newEvent.start.getHours() < 6 || newEvent.end.getHours() > 22) {
    return false;
  }

  if (newEvent.type === calendarResourceTypes.EXPERTE || newEvent.type === calendarResourceTypes.GRUPPENRAUM) {
    const onDayEvent = isSameDay(newEvent.start, newEvent.end);
    if (!onDayEvent) {
      return false;
    }
  }

  const eventDays = eachDayOfInterval(newEvent).length;

  return eventDays <= ablauf.length;
}
